import { useTheme } from '../../context/ThemeContext.jsx';

export default function ExternalResults({
  debouncedSearch, externalLoading, externalResults,
  previewText, previewSize, extFontsReady,
  importingFont, onImportExternal,
}) {
  const { theme } = useTheme();

  if (!debouncedSearch || (!externalLoading && externalResults.google.length === 0 && externalResults.adobe.length === 0)) {
    return null;
  }

  return (
    <div style={{ padding: '8px 24px 24px' }}>
      <div style={{
        fontSize: 10, fontWeight: 700, color: theme.textFaint,
        textTransform: 'uppercase', letterSpacing: '0.08em',
        marginBottom: 12, paddingTop: 8,
        borderTop: `1px solid ${theme.borderLight}`,
      }}>
        Also in external catalogs
        {externalLoading && <span style={{ fontWeight: 400, marginLeft: 8 }}>Loading…</span>}
      </div>

      {externalResults.google.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#4285F4', marginBottom: 6, letterSpacing: '0.04em' }}>
            GOOGLE FONTS · {externalResults.google.length} match{externalResults.google.length !== 1 ? 'es' : ''}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {externalResults.google.map((font, idx) => (
              <div key={font.family} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '8px 12px',
                background: theme.bgInput,
                border: `1px solid ${theme.borderLight}`,
                borderRadius: 7,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 2 }}>{font.family}</div>
                  <div style={{
                    fontFamily: `'${font.family}', sans-serif`,
                    fontSize: Math.round(previewSize * 0.75),
                    color: theme.textSecondary,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    opacity: extFontsReady[`g:${idx}`] ? 1 : 0.25,
                    transition: 'opacity 0.3s',
                  }}>{previewText}</div>
                </div>
                <button
                  onClick={() => onImportExternal(font, 'google')}
                  disabled={importingFont === font.family}
                  style={{
                    background: theme.bgElevated,
                    border: `1px solid ${theme.borderHover}`,
                    borderRadius: 6, color: theme.textSecondary,
                    fontSize: 10, fontWeight: 600, padding: '4px 10px',
                    cursor: importingFont === font.family ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit', flexShrink: 0,
                    opacity: importingFont === font.family ? 0.5 : 1,
                  }}
                >{importingFont === font.family ? '…' : '+ Import'}</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {externalResults.adobe.length > 0 && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#E02020', marginBottom: 6, letterSpacing: '0.04em' }}>
            ADOBE FONTS · {externalResults.adobe.length} match{externalResults.adobe.length !== 1 ? 'es' : ''}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {externalResults.adobe.map((font) => {
              const genericStack = {
                'serif': 'Georgia, "Times New Roman", serif',
                'sans-serif': 'system-ui, Helvetica, Arial, sans-serif',
                'display': '"Palatino Linotype", Palatino, Georgia, serif',
                'handwriting': 'cursive',
                'monospace': '"Courier New", Courier, monospace',
              }[font.category] || 'Georgia, serif';
              return (
                <div key={font.family} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '8px 12px',
                  background: theme.bgInput,
                  border: `1px solid ${theme.borderLight}`,
                  borderRadius: 7,
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 3 }}>
                      <div style={{ fontSize: 11, fontWeight: 600 }}>{font.family}</div>
                      {font.designer && (
                        <div style={{ fontSize: 9, color: theme.textFaint }}>by {font.designer}</div>
                      )}
                    </div>
                    <div style={{
                      fontFamily: genericStack,
                      fontStyle: 'italic',
                      fontSize: Math.round(previewSize * 0.75),
                      color: theme.textSecondary,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{previewText}</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                    <button
                      onClick={() => onImportExternal(font, 'adobe')}
                      disabled={importingFont === font.family}
                      style={{
                        background: theme.bgElevated,
                        border: `1px solid ${theme.borderHover}`,
                        borderRadius: 6, color: theme.textSecondary,
                        fontSize: 10, fontWeight: 600, padding: '4px 10px',
                        cursor: importingFont === font.family ? 'not-allowed' : 'pointer',
                        fontFamily: 'inherit',
                        opacity: importingFont === font.family ? 0.5 : 1,
                      }}
                    >{importingFont === font.family ? '…' : '+ Import'}</button>
                    {font.slug && (
                      <a
                        href={`https://fonts.adobe.com/fonts/${font.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          fontSize: 9, color: theme.textFaint,
                          textDecoration: 'none',
                        }}
                        onClick={e => e.stopPropagation()}
                      >View on Adobe Fonts ↗</a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
